import { FC, Ref, useRef } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { COURSE_ID_QUERY_PARAM } from "components/constants";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuDivider, MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { CopySendArrowIcon, FolderIcon, PencilEditIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { courseGroupingSelectors } from "store/selectors/base";
import { selectSortedTermSectionsByCourse } from "store/selectors/section";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";

interface CourseOptionsMenuProps extends SidebarOptionsMenuProps {
  course: CourseType;
  onRename: () => void;
}

export const CourseOptionsMenu: FC<CourseOptionsMenuProps> = ({
  course,
  onRename,
  className,
  eventPrefix,
  kind,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const groupings = useAppSelector(courseGroupingSelectors.selectAll);
  const termSections = useAppSelector((s) => selectSortedTermSectionsByCourse(s, course?.id));
  const hasSections = termSections.some((term) => term.sections.length > 0);
  const filteredGroupings = groupings.filter((grouping) => grouping.id !== course.grouping_id);
  const { deleteQueryParams } = useQueryParamsManager();

  const handleMoveToGrouping = async (groupingId: string | null) => {
    await storeApi.courses.partial_update({
      id: course.id,
      grouping_id: groupingId,
    });

    toastSuccessMessage(t("home.toasts.course_moved_one"));
    menuRef.current?.closeMenu();
  };

  const handleDelete = async () => {
    if (hasSections) {
      return;
    }

    const didDelete = await showConfirmationModal({
      title: t("confirm_delete_modal.delete_item", { item: t("glossary.course") }),
      labelText: t("confirm_delete_modal.delete_label", { name: course.code }),
    });

    if (didDelete) {
      await storeApi.courses.destroy(course.id);

      toastSuccessMessage(t("home.toasts.course_deleted"));

      deleteQueryParams([COURSE_ID_QUERY_PARAM]);
    }
  };

  const moveToSubMenu = (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
      }
      overflow="auto"
      withArrow
    >
      <div className="m-2 min-w-[200px]">
        <div className="pb-2 text-black-tint-40">{t("sidebar_options_menu.move_course_to")}</div>

        {filteredGroupings.map((grouping) => (
          <MenuItem key={grouping.id} onClick={() => handleMoveToGrouping(grouping.id)}>
            <IconText iconStart={<FolderIcon />} text={grouping.title} />
          </MenuItem>
        ))}

        {course.grouping_id && (
          <MenuItem onClick={() => handleMoveToGrouping(null)}>
            {t("sidebar_options_menu.uncategorized")}
          </MenuItem>
        )}
      </div>
    </SubMenu>
  );

  return (
    <SidebarOptionsMenu
      className={className}
      direction="bottom"
      eventPrefix={eventPrefix}
      instanceRef={menuRef}
      kind={kind}
      optionsMenuActions={{
        onDelete: handleDelete,
        isDeleteDisabled: hasSections,
        deleteTooltipText: hasSections
          ? t("sidebar_options_menu.cannot_delete_course_with_sections")
          : undefined,
      }}
      size="s"
    >
      <MenuItem onClick={onRename}>
        <IconText iconStart={<PencilEditIcon />} text={t("sidebar_options_menu.rename_course")} />
      </MenuItem>

      <MenuDivider />

      {moveToSubMenu}
    </SidebarOptionsMenu>
  );
};
